/* eslint-disable  react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { FieldSkeleton } from "components/SkeletonLoading";
import apiRequest, { getErrorCode } from "utils/apiRequestWithErrorCode";

import { MXRecordResponse } from "@dashboard-v3/api";

interface Props {
  value: string;
  domainName: string;
  onChange: (server: string) => void;
  hasError: boolean;
  errorMsg: string;
}

type State = {
  loading: boolean;
  mxRecords: string[];
};

export default function ServerSelector(props: Props) {
  const { t } = useTranslation("orgAndDomains");
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState<State>({
    loading: true,
    mxRecords: [],
  });

  function updateState(update: Partial<State>) {
    setState(prev => ({ ...prev, ...update }));
  }

  useEffect(() => {
    async function getServerValue() {
      const { domainName } = props;
      updateState({ loading: true });

      try {
        const res = await apiRequest<MXRecordResponse>(
          "GET",
          `/domains/mx-lookup/${domainName}`
        );
        updateState({ mxRecords: [res.mxRecord] });
      } catch (e) {
        const errorCode = getErrorCode(e);
        let errorKey = "error.domainServer.mxRecordFetchError";

        if (errorCode === "RECORD_NOT_FOUND") {
          errorKey = "error.domainServer.mxNotFound";
        }

        enqueueSnackbar(t(errorKey, { domainName }), {
          variant: "error",
        });
        updateState({ mxRecords: [] });
      } finally {
        updateState({ loading: false });
      }
    }

    getServerValue();
  }, [props.domainName]);

  function onChange(_, value: string) {
    props.onChange(value);
  }

  if (state.loading) {
    return (
      <div data-testid="serverInput-skeleton">
        <FieldSkeleton fieldProps={{ style: { marginBottom: 16 } }} />
      </div>
    );
  }

  return (
    <Autocomplete
      freeSolo
      options={state.mxRecords}
      value={props.value}
      onChange={onChange}
      onInputChange={onChange}
      renderOption={opt => opt}
      getOptionLabel={opt => opt || ""}
      renderInput={params => (
        <TextField
          {...params}
          style={{ marginBottom: "16px" }}
          type="text"
          name="server"
          label={t("list.server")}
          variant="outlined"
          fullWidth
          error={props.hasError}
          helperText={props.hasError && props.errorMsg}
          inputProps={{
            ...params.inputProps,
            "data-testid": "serverInput",
          }}
        />
      )}
    />
  );
}
